import DOMPurify from 'dompurify';

/**
 * Shared constants and utilities for rich HTML rendering across all quiz/exam players.
 * This centralises the CSS scoping, sanitisation, and style injection logic
 * so that uploaded HTML styles are preserved consistently.
 */

export const QUIZ_HTML_SCOPE = "quiz-html-scope";

export const RICH_HTML_TAGS = [
  'svg', 'path', 'circle', 'rect', 'ellipse', 'line', 'polygon', 'polyline',
  'text', 'g', 'defs', 'marker', 'tspan', 'use',
  'div', 'span', 'b', 'strong', 'em', 'i', 'br',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p',
  'img', 'figure', 'figcaption',
  'table', 'tr', 'td', 'th', 'thead', 'tbody', 'tfoot', 'caption', 'colgroup', 'col',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'small', 'sup', 'sub', 'mark', 'abbr',
  'section', 'article', 'aside', 'details', 'summary',
  'hr', 'pre', 'code', 'blockquote',
];

export const RICH_HTML_ATTRS = [
  // SVG
  'viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'stroke-linecap',
  'stroke-linejoin', 'stroke-dasharray', 'cx', 'cy', 'r', 'rx', 'ry',
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'width', 'height', 'points', 'd',
  'transform', 'text-anchor', 'font-family', 'font-size', 'font-weight',
  'opacity', 'marker-end', 'markerWidth', 'markerHeight', 'refX', 'refY',
  'orient', 'stroke-opacity', 'fill-opacity', 'image-rendering',
  // HTML
  'style', 'class', 'id', 'src', 'alt', 'loading', 'title', 'href', 'target', 'rel',
  'colspan', 'rowspan', 'scope', 'data-*',
];

/**
 * Sanitise HTML while preserving rich formatting (SVGs, tables, styled divs).
 * Use `allowStyleTag = true` only for intro/landing page HTML that embeds <style> blocks.
 */
export function sanitizeRichHtml(html: string, allowStyleTag = false): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ADD_TAGS: allowStyleTag ? [...RICH_HTML_TAGS, 'style'] : RICH_HTML_TAGS,
    ADD_ATTR: RICH_HTML_ATTRS,
  });
}

/**
 * Prefix all CSS selectors with the quiz scope class to prevent global style leaks.
 * Handles @media, @keyframes, :root/body selectors, and comma-separated selectors.
 */
export function scopeQuizCss(css: string, scopeClass = QUIZ_HTML_SCOPE): string {
  return css.replace(/(^|})\s*([^@}{][^{]+)\{/g, (_match, prefix: string, selectorGroup: string) => {
    const scopedSelectors = selectorGroup
      .split(',')
      .map((selector) => selector.trim())
      .filter(Boolean)
      .map((selector) => {
        if (selector.includes(`.${scopeClass}`)) return selector;
        if (selector === ':root' || selector === 'body' || selector === 'html') return `.${scopeClass}`;
        if (selector === 'body::before' || selector === 'html::before') return `.${scopeClass}::before`;
        if (selector === 'body::after' || selector === 'html::after') return `.${scopeClass}::after`;
        if (/^(from|to|\d+%)$/.test(selector)) return selector;
        return `.${scopeClass} ${selector}`;
      })
      .join(', ');

    return `${prefix} ${scopedSelectors} {`;
  });
}

/**
 * Inject scoped CSS into the document head. Returns a cleanup function.
 * Use in a useEffect to auto-remove on unmount.
 */
export function injectScopedStyles(css: string, attrName = "data-quiz-styles", scopeClass?: string): () => void {
  const styleEl = document.createElement("style");
  styleEl.setAttribute(attrName, "true");
  styleEl.textContent = scopeQuizCss(css, scopeClass);
  document.head.appendChild(styleEl);
  return () => {
    if (styleEl.parentNode) document.head.removeChild(styleEl);
  };
}
